import { Component, ElementRef, EventEmitter, HostListener, Injector, Input, NgZone, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { AbstractComponent, RoleUserEnum, SidebarService, UtilHelper } from 'lib-trend-core';
import { debounceTime, filter, fromEvent, Subscription, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

/** How many days a sidebar item keeps its "new" badge after being released. */
const NEW_BADGE_DAYS = 30;

/** How long the menu scrollbar stays visible after the last scroll event. */
const SCROLLBAR_IDLE_MS = 800;

/** Release dates of the sidebar items highlighted with the "new" badge. */
const RELEASED_AT = {
  campaigns: '2026-09-02',
  flowBuilder: '2026-09-04',
  wallets: '2026-09-04',
};

interface MenuItem {
  label: string;
  icon?: string;
  route?: string;
  isActive: boolean;
  subItems?: MenuItem[];
  subItemLabel?: string;
  isSidenavTitle?: boolean;
  badge?: string;
}

@Component({
    selector: 'sidebar-component',
    // templateUrl: `./sidebar.component.html`,
    templateUrl: `./sidebar.v2.component.html`,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SideBarComponent extends AbstractComponent implements OnInit {

  @Input() sidenav!: MouseEvent;
  @Output() toggleSidebarEmit: EventEmitter<boolean> = new EventEmitter<boolean>();

  sidebarMenu: MenuItem[] = [];

  isExpanded = true;
  private resizeSubscription!: Subscription;
  private menuScrollSubscription?: Subscription;

  @ViewChild('menuContainer', { static: true }) private menuContainer!: ElementRef<HTMLElement>;

  menuItems = [
    { icon: 'home', text: 'Home' },
    { icon: 'account_circle', text: 'Profile' },
    { icon: 'settings', text: 'Settings' },
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const backdrop = document.getElementById('custom-backdrop');

    if (backdrop && target === backdrop) {
      this.closeSidebar();
    }
  }

  private manualToggle!: any

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private renderer: Renderer2,
    override router: Router,
    private sidebarService: SidebarService,
    private ngZone: NgZone,
  ) {
    super(injector)
  }

  ngOnInit(): void {
    this.manualToggle = this.sidebarService.getManualToggle
    this.setInitialSidebar();
    this.onResizeScreen();
    this.listenToResize();
    this.listenToMenuScroll();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.onResizeScreen());


    this.sidebarMenu = [
      {
        label: 'layout.sidebar.dashboard',
        icon: 'ri-dashboard-line',
        route: '/home',
        isActive: this.isAttendant() || this.isSupervisor() || this.isAdmin()
      },
      {
        label: 'layout.sidebar.crm',
        icon: 'ri-filter-3-fill',
        route: '/crm',
        isActive: this.getModuleCRM() && (this.isAttendant() || this.isSupervisor() || this.isAdmin()),
        subItemLabel: 'crm',
        subItems: [
          // {
          //   label: 'layout.sidebar.dashboard',
          //   icon: 'ri-filter-3-fill',
          //   route: '/crm',
          //   isActive: true
          // },
          {
            label: 'layout.sidebar.kanban',
            icon: 'ri-filter-3-fill',
            route: '/kanban',
            isActive: true
          },
          {
            label: 'layout.sidebar.products',
            icon: 'ri-shopping-bag-2-line',
            route: '/kanban/product',
            isActive: !this.isAttendant()
          },
          {
            label: 'layout.sidebar.customers',
            icon: 'ri-user-3-line',
            route: '/kanban/customer',
            isActive: true
          },
          {
            label: 'layout.sidebar.lossReasons',
            icon: 'ri-close-circle-line',
            route: '/kanban/loss-reason',
            isActive: !this.isAttendant()
          },
          {
            label: 'layout.sidebar.crmLists',
            icon: 'ri-list-settings-line',
            route: '/kanban/crm-lists',
            // Mirrors AuthGuardAdmin (`roles.includes(ADMIN)`), the rule the route and
            // `PUT /api/company/crm-lists` both enforce (E2 RF-09).
            isActive: this.getRoles()?.includes(RoleUserEnum.ADMIN) ?? false
          },
          {
            label: 'layout.sidebar.leadQuality',
            icon: 'ri-medal-line',
            route: '/kanban/lead-quality',
            isActive: this.getRoles()?.includes(RoleUserEnum.ADMIN) ?? false
          },
          {
            label: 'layout.sidebar.tasks',
            icon: 'ri-task-line',
            route: '/task',
            isActive: true
          },
        ]
      },
      {
        label: 'layout.sidebar.attendances',
        icon: 'ri-message-3-line',
        route: '/attendance',
        isActive: this.isAttendant() || this.isSupervisor() || this.isAdmin(),
        subItemLabel: 'attendance',
        subItems: [
          {
            label: 'layout.sidebar.panel',
            icon: 'ri-filter-3-fill',
            route: '/attendance/panel',
            isActive: true
          },
          {
            label: 'layout.sidebar.summary',
            icon: 'ri-task-line',
            route: '/attendance',
            isActive: true
          },
          {
            label: 'layout.sidebar.monitoring',
            icon: 'mdi-monitor-dashboard',
            route: '/attendance/monitoring',
            isActive: (this.isSupervisor() || this.isAdmin())
          },
        ]
      },
      {
        label: 'layout.sidebar.campaigns',
        icon: 'ri-send-plane-line',
        route: '/campaign',
        isActive: this.getModuleCampaign() && (this.isSupervisor() || this.isAdmin()),
        badge: this.newBadge(RELEASED_AT.campaigns)
      },
      {
        label: 'layout.sidebar.contacts',
        icon: 'ri-contacts-book-2-line',
        route: '/contact',
        isActive: (((this.isAttendant() && this.getCurrentUserUser().viewContacts) || (this.isSupervisor() && this.getCurrentUserUser().viewContacts)) || (this.isAdmin()))
      },
      {
        label: 'layout.sidebar.tracking',
        icon: 'ri-map-pin-line',
        route: '/report/view-tracking',
        isActive: this.getModuleTracking() && (this.isSupervisor() || this.isAdmin())
      },
      {
        label: 'layout.sidebar.wallets',
        icon: 'ri-coins-line',
        route: '/wallet',
        isActive: this.isAdmin(),
        badge: this.newBadge(RELEASED_AT.wallets)
      },
      {
        label: 'layout.sidebar.aiAgents',
        icon: 'mdi mdi-robot-outline',
        route: '/agents',
        isActive: this.isAdmin() && (this.getModuleOpenAI() || this.getModuleTrendGPT()),
        subItemLabel: 'agentesia',
        subItems: [
          {
            label: 'layout.sidebar.openai',
            icon: 'mdi mdi-robot-outline',
            route: '/agents/openai',
            isActive: this.getModuleOpenAI()
          },
          {
            label: 'layout.sidebar.trendgpt',
            icon: 'mdi mdi-robot-outline',
            route: '/agents/trendgpt',
            isActive: this.getModuleTrendGPT()
          },
        ]
      },
      {
        label: 'layout.sidebar.flows',
        icon: 'ri-flow-chart',
        route: '/flow',
        isActive: this.getModuleFlow() && this.isAdmin(),
        badge: this.newBadge(RELEASED_AT.flowBuilder)
      },
      {
        label: 'layout.sidebar.integrations',
        icon: 'ri-plug-2-line',
        route: '/integrations',
        isActive: this.getModuleIntegrations() && (((this.isAttendant() && this.getCurrentUserUser().viewIntegrations) || (this.isSupervisor() && this.getCurrentUserUser().viewIntegrations)) || (this.isAdmin())),
        subItemLabel: 'integrations',
        subItems: [
          {
            label: 'layout.sidebar.webhook',
            icon: 'mdi mdi-robot-outline',
            route: '/integrations/webhook/list',
            isActive: this.getModuleWebhook()
          },
          {
            label: 'layout.sidebar.pipedrive',
            icon: 'mdi mdi-robot-outline',
            route: '/integrations/pipedrive/list',
            isActive: this.getModulePipedrive()
          },
          {
            label: 'layout.sidebar.agendor',
            icon: 'mdi mdi-robot-outline',
            route: '/integrations/agendor/list',
            isActive: this.getModuleAgendor()
          },
          {
            label: 'layout.sidebar.santri',
            icon: 'mdi mdi-robot-outline',
            route: '/integrations/santri/list',
            isActive: this.getModuleWebhookSantri()
          },
        ]
      },
      {
        label: 'layout.sidebar.reports',
        icon: 'ri-line-chart-line',
        route: '/report',
        isActive: (this.isSupervisor() || this.isAdmin())
      },
      {
        label: 'layout.sidebar.settings',
        icon: 'ri-settings-3-line',
        route: '/config',
        isActive: (this.isSupervisor() || this.isAdmin()),
        subItemLabel: 'configs',
        subItems: [
          {
            label: 'layout.sidebar.channels',
            icon: 'ri-exchange-box-line',
            route: '/channel',
            isActive: this.isAdmin()
          },
          {
            label: 'layout.sidebar.users',
            icon: 'ri-team-line',
            route: '/user',
            isActive: this.isAdmin()
          },
          {
            label: 'layout.sidebar.responses',
            icon: 'ri-edit-box-line',
            route: '/response',
            isActive: this.isSupervisor() || this.isAdmin()
          },
          {
            label: 'layout.sidebar.qualification',
            icon: 'ri-price-tag-3-line',
            route: '/qualification',
            isActive: (this.isSupervisor() || this.isAdmin())
          },
          {
            label: 'layout.sidebar.timezone',
            icon: 'ri-settings-3-line',
            route: '/config',
            isActive: (this.isSupervisor() || this.isAdmin())
          },
        ]
      }
    ];
  }

  /**
   * Returns the "new" badge key while the item is inside its highlight window
   * (NEW_BADGE_DAYS after its release date), and undefined once it expires.
   */
  newBadge(releasedAt: string): string | undefined {
    const expiresAt = new Date(releasedAt);
    if (isNaN(expiresAt.getTime())) {
      return undefined;
    }
    expiresAt.setDate(expiresAt.getDate() + NEW_BADGE_DAYS);
    return Date.now() < expiresAt.getTime() ? 'layout.sidebar.badgeNew' : undefined;
  }

  override ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    this.menuScrollSubscription?.unsubscribe();
  }

  setInitialSidebar() {
    const width = window.innerWidth;
    let newState: string;

    if (width < 768) {
      newState = 'full';
    } else if (width < 992) {
      newState = 'condensed';
    } else {
      newState = 'default';
    }

    this.renderer.setAttribute(
      document.documentElement,
      'data-sidenav-size',
      newState,
    );

    document.getElementsByTagName('body')[0].classList.add('show');
    document.documentElement.classList.add('menuitem-active');
  }

  onResizeScreen(): void {
    const url = this.router.url;
    const width = window.innerWidth;
    let newState: string | null = null;

    if (url.includes('/attendance/panel') || url.includes('/flow/edit/')) {
      if (width < 768) {
        newState = 'full';
      } else if (!this.manualToggle) {
        newState = null;
      }
    } else {
      if (width < 768) {
        newState = 'full';
      } else if (width < 992) {
        newState = 'condensed';
      } else {
        newState = 'default';
      }
    }

    if (newState) {
      this.updateSidebarSize(newState);
    }
  }

  private updateSidebarSize(newState: string): void {
    const currentState = document.documentElement.getAttribute('data-sidenav-size');
    if (currentState !== newState) {
      this.renderer.setAttribute(document.documentElement, 'data-sidenav-size', newState);
    }
  }

  closeSidebar(): void {
    const width = window.innerWidth;

    if (width > 768) {
      return;
    }

    const backdrop = document.getElementById('custom-backdrop');

    if (!backdrop) return;

    document.documentElement.classList.remove('sidebar-enable');
    document.body.style.overflow = '';
    backdrop.remove();
  }

  private listenToResize(): void {
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(200))
      .subscribe(() => this.onResizeScreen());
  }

  /**
   * Overlay-style menu scrollbar, like the page's own: `.is-scrolling` reveals the
   * thumb while the menu scrolls and is dropped after a short idle. Runs outside
   * Angular so scroll events never trigger change detection.
   */
  private listenToMenuScroll(): void {
    const container = this.menuContainer.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      this.menuScrollSubscription = fromEvent(container, 'scroll', { passive: true })
        .pipe(
          tap(() => this.renderer.addClass(container, 'is-scrolling')),
          debounceTime(SCROLLBAR_IDLE_MS),
        )
        .subscribe(() => this.renderer.removeClass(container, 'is-scrolling'));
    });
  }

  goToWebAdmin() {
    const idUser = super.getIDCurrentUser();
    const idCompany = super.getIDCurrentCompany();
    const base64 = UtilHelper.generateTokenTowardsApps(idUser, idCompany);
    window.open(`${environment.trendWebAdminUrl}/redirect?token=${base64}`, '_blank');
  }

  toggleSidenav() {
    this.manualToggle = !this.manualToggle;
    this.sidebarService.setManualToggle(this.manualToggle);
    this.sidebarService.toggleExpandedState();
  }

}
